import { RefObject } from 'react'
import { IGroupVotingButton, IVotingButton } from './voting'
import { ArtistProps } from 'components/modules/Calendar/types'
import { NavigationHeaderProps } from '../components/modules/NavigationHeader/types'
import { breakpoints } from 'utils/styles/breakpoints'

export type BreakpointKey = keyof typeof breakpoints

export type Nullable<T> = T | null

export interface FetchedStories {
  pages: PageContent[]
}

export interface PageContent {
  blocks: Array<ComponentItem>
  seo?: SeoProps
  slug: string
  title?: string
  backgroundImageXl?: AssetType
}

export interface PageProps {
  content: {
    body: PageContent
  }
  preview?: boolean
  lostandfoundData?: string | null
}

export interface Story {
  name: string
  content: {
    body: PageContent
  }
}

export interface PageData {
  preview: boolean
  slug: string
  story: Story
  lostandfoundData?: string | null
}

export interface ResolvedPagesData {
  pages: PageData[]
}

export interface Twitter {
  handle?: string
  site?: string
  cardType?: string
}

export interface VideoProps {
  id?: string
  autoPlay?: boolean
  backgroundVideoXl?: AssetVideoType
  backgroundVideoSm?: AssetVideoType
  className?: string
  posterImageXl?: AssetType
  posterImageSm?: AssetType
  videoLoop?: boolean
  externalVideoRef?: RefObject<HTMLVideoElement>
  handlePlaybackFailed?: (status: boolean) => void
  onMediaLoad?: () => void
  preload?: 'auto' | 'none'
  isChildOfHeroCarousel?: boolean
  isPaused?: boolean
  vimeoIdXl?: string
  vimeoIdSm?: string
  vimeoId?: string
  allowPause?: boolean
  allowVolume?: boolean
  restartOnLeaveViewport?: boolean
  autoPlayDelay?: number
  allowFullscreen?: boolean
  isInModal?: boolean
}

export type RichTextType = { html: string | null } | null

export interface SeoProps {
  allowSearchEnginesToShowThisPage?: boolean
  canonical?: string
  defaultTitle?: string
  facebook?: { appId: string }
  ogImage?: {
    url: string
  }
  ogTitle?: string
  ogType?: string
  ogDescription?: string
  metaDescription?: string
  seoTitle?: string
  shouldSearchEnginesFollowLinks?: boolean
  twitter?: Twitter
  canonicalUrl?: string
}

export interface HeroGeneralInfoProps {
  id?: string
  componentName?: string | 'HeroGeneralInfo'
  headline: string
  copy: RichTextType
  infoItems: Array<HeroInfoBarLinkProps>
  backgroundImageXl: AssetType
  backgroundImageSm: AssetType
  showBackgroundImage: boolean
}

export interface NotificationBannerProps {
  id?: string
  copy: RichTextType
  ctaPrimary?: ButtonProps
  showBanner: boolean
}

export interface ClassificationProps {
  id?: string
  title: string
  description?: string
  ratingNumber?: number
  ratingImage?: AssetType
}

// @TODO: determine the TRUE alt of the three
export interface ImageProps {
  alt?: string
  fileName?: string
  id?: string
  image: AssetType
  componentName?: string | 'Image'
  width?: number
  height?: number
}

export type ImageComponentProps = Pick<ImageProps, 'id' | 'alt' | 'image' | 'componentName'>

export type AssetType = {
  id?: string
  altText?: string
  url: string
  fileName?: string
  width?: number
  height?: number
}

export type AssetVideoType = Pick<AssetType, 'fileName' | 'url'>
export interface HeroInfoBarLinkProps {
  image: ImageProps
  anchorText: string
  url: string
}

export interface InfoBarProps {
  id?: string
  headline: string
  highlightedWords?: string
  gradientColor?: string
  displayGradient?: boolean
  gradientPlacementOnTop?: boolean
  infoBarSections: Array<InfoBarSectionProps>
  image: AssetType
}

export interface InfoBarSectionProps {
  cta: ButtonProps
  defaultImg?: ImageProps
  description: RichTextType
  eyebrow?: string
  icon?: AssetType
  iconHeight?: number
  iconWidth?: number
  id?: string
  mobileImage?: ImageProps
  subheadlineColor?: string
  subheadline: string
}

export const EducationLevels: string[] = [
  '',
  '',
  '',
  '',
  '4th Grade',
  '5th Grade',
  '6th Grade',
  '7th Grade',
  '8th Grade',
  '9th Grade',
  '10th Grade',
  '11th Grade',
  '12th Grade',
  'Freshman',
  'Sophomore',
  'Junior',
  'Senior',
  'Grad Student'
]

export interface URLProps {
  //anchor: ? @TODO: determine what `anchor` prop type is
  cached_url?: string
  fieldtype: 'multilink' | string // @TODO: determine other possible values
  id?: string
  linktype: 'story' | 'url'
  url: string
}

export type SubNavLinksType = {
  id: string
  isTargetBlank: boolean
  title: string
  url: string
  hasDivider?: boolean
  hasAccentDivider?: boolean
  groupTitle?: string
  sectionLinks?: Array<{
    title: string
    url: string
  }>
}

export interface NavigationMenuItemProps {
  id: string
  componentName: string | 'NavigationMenuItem'
  hasDivider?: boolean
  hasAccentDivider?: boolean
  isTargetBlank?: boolean
  submenuImage?: AssetType
  submenuImageTitle?: string
  submenuImageSubtitle?: string
  submenuRedirectUrl?: string
  subNavLinks?: SubNavLinksType[]
  title: string
  url: string
}

export interface CarouselSlideProps {
  componentName?: string | 'CarouselSlide'
  defaultImg: ImageProps
  description?: string
  handleImageClick: (event: React.MouseEvent<HTMLImageElement>) => void
  id?: string
  imageDescription?: string
  imageTitle?: string
  mobileImage?: ImageProps
  openImageInModal?: boolean
  title?: string
}

export type ArtistIntroCardType = {
  id?: string
  componentName?: string | 'ArtistIntroCard'
  image?: AssetType
  logoImage?: AssetType
  title: string
}

export interface DoesNotExistProps {
  backgroundImage?: AssetType
  buttonGroup: Array<ButtonProps>
  id?: string
}

export interface GtmTypes {
  businessUnit?: string | 'entertainment'
  clickPreviousPv?: string | 'shows' | 'other_events'
  clickType?: string | 'cta' | 'navigation'
  clickSource?: string
  clickTracking?: string
  event?: string | 'click_buy' | 'click_content' | 'click_nav' | 'click_thumbnail' | 'pageview'
  eventData?: {
    eventCategory?: string | null
    eventDate?: string | null
    eventType?: string | null
    ticketmasterEventId?: string
    venueName?: 'sphere'
  }
  linkLocation?: string
  linkModule?: string
  linkPosition?: string | null
  linkType?: string | 'internal' | 'external' | 'interactive' | 'fevo'
  navType?: string | 'header' | 'footer'
  pageType?: string
  skipEvent?: boolean
}

export type ClickEvt = React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>

export type ButtonBackgroundColors = 'primary' | 'light' | 'gold'

export interface ButtonProps {
  analytics?: GtmTypes
  anchorLink?: string
  color?: ButtonBackgroundColors
  componentName?: string | 'Button'
  disabled?: boolean
  fill?: string | 'hollow' | 'solid' | 'text'
  id?: string
  isFullWidth?: boolean
  isLink?: boolean
  isTargetBlank?: boolean
  justify?: string | 'left' | 'center' | 'right'
  modal?: Omit<ModalProps, 'handleModalClose'>
  onClick?: ((e: ClickEvt) => void) | null
  size?: ButtonSizeType
  title: string
  url?: string | null
  icon?: IconNavType | null
  dropdownOptions?: MenuDropDownOption[]
  onOptionSelect?: (value: string, option: DropDownOption) => void
  openModalOnClick?: boolean
}

export type ButtonSizeType = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export type ButtonGroupProps = {
  analytics?: {
    linkLocation?: string
    linkModule: string
    parentHeadline?: string
  }
  buttons?: ButtonProps[]
  buttonSizeOverride?: ButtonSizeType
  headline?: string
  isFullWidthOverride?: boolean
  justifyOverride?: AlignmentType
  layout?: 'stacked' | 'inline' | 'split'
}

export type HeadingJustifyType = string | 'left' | 'center'

export interface HeadingProps {
  children?: React.ReactNode | string
  color?: string | 'accent' | 'dark' | 'light' | 'lightest' | 'primary' | 'primary-light'
  componentName?: string | 'ContentHeading'
  id?: string
  level?: string | number | 1 | 2 | 3 | 4 | 5 | 6
  levelDisplay?: 1 | 2 | 3 | 4 | 5 | 6 | 'display' | 'display-lg' | 'eyebrow'
  justify?: HeadingJustifyType
  noMarginTop?: boolean
  noMarginBottom?: boolean
  showHeading?: boolean
  title?: string
}

export interface EventProps {
  category: Array<string>
  date_time: number
  event_type: Array<string>
  formatted_start_time: string
  host_url: string
  id: string
  is_sold_out: boolean
  start_time: string
  name: string
  price: PriceProps
  spanMultipleDays?: boolean
  end_date?: string
  timezone?: string
}

interface EEArtistProps {
  id: string
  artistImage: ImageProps
  artistName: string
}

interface PriceProps {
  min: number
  max: number
}

export interface EventDateProps {
  category: string
  date_time: string
  end_date: string
  spanMultipleDays: boolean
  artists: Array<EEArtistProps>
  date: number
  event_type: Array<string>
  events: Array<EventProps>
  formatted_start_time: string
  hidePricing: boolean
  host_url: string
  id: string
  is_best_value: boolean
  is_holiday: boolean
  is_on_sale: boolean
  is_sold_out: boolean
  is_sponsored_day: boolean
  name: string
  price: PriceProps
  sponsors: Array<string>
  venue_id: string
}

export interface EventDatesAPIResponseProps {
  results: Array<EventDateProps>
}

export interface NavProps {
  noOfMonths: number
  startDate: string
}

export interface MonthProps {
  label: string
  value: string
  shortLabel: string
}

export interface DropDownFilter {
  calendarData: Map<string, object> | object
  calendarFilterItems: Array<MonthProps>
  selectedMonth: MonthProps
  allEvents: Array<EventDateProps>
}

export interface CalendarMultiArtistProps {
  heading: string
  backgroundImage?: AssetType
  eventDetailsDisclaimer: RichTextType
  artists: Array<ArtistProps>
  groupTicketsOfferText?: RichTextType
  iconNavigationGroup?: IconNavigationGroupProps
}

export type CalendarThemeType = 'accent' | 'primary'

export interface CalendarSingleArtistProps {
  id?: string
  calendarId?: string
  componentName?: string | 'Calendar'
  heading?: string
  overrideApiSoldOutStatus?: boolean
  soldOutConfirmationModalText?: string
  title?: string
  hidePricing?: boolean
  seo?: SeoProps
  artist: ArtistProps
  eventDetailsDisclaimer?: RichTextType
  groupTicketsOfferText?: RichTextType
  theme: CalendarThemeType
}

export interface EventDatesProps {
  eventDates: Array<EventDateProps>
}

export interface ParagraphProps {
  id?: string
  color?: string | 'light' | 'lightest'
  componentName?: string | 'Paragraph'
  fontSize?: string | 'lg' | 'md' | 'sm' | 'xs'
  justify?: 'center' | 'left'
  noMarginBottom?: boolean
  text: RichTextType | undefined
  readMoreText?: RichTextType
}

export interface FormProps {
  componentName?: string | 'Form'
  formId: string
  onCloseModalHandler?: () => void
  id?: string
  inputBgColor?: 'black' | 'transparent' | 'white'
  isChildOfModal?: boolean
  submitButtonBgColor?: 'white'
  submitButtonText?: string
  fieldEnhancements?: FieldEnhancements
  floatingLabel?: boolean
  narrowForm?: boolean
  successModalView?: ModalViewFormSuccessProps
  onSuccess?: (view: ModalViewFormSuccessProps) => void
  onError?: (view: ModalViewFormSuccessProps) => void
}

export interface FieldValidationEnhancement {
  validationUi?: boolean
  validationMessage?: string
}

export interface SelectFieldEnhancement extends FieldValidationEnhancement {
  useSelect2Ui?: boolean
}

/**
 * Per-field enhancement configuration for form fields.
 * Commonly used keys: `select`, `text`, `phone`, `email`.
 */
export interface FieldEnhancements {
  [key: string]: FieldValidationEnhancement | SelectFieldEnhancement | unknown
}

export interface ContentModuleSectionProps {
  id?: string
  blocks: Array<ParagraphProps | ButtonProps | HeadingProps | ImageProps | FormProps | ModalProps>
  showDividerAtBottom?: boolean
  showDividerAtTop?: boolean
  dividerFullWidth?: boolean
  contentModuleDivId?: string
}

export interface ContentModuleProps {
  id?: string
  componentName?: string | 'Module'
  moduleSections: Array<ContentModuleSectionProps>
}

export interface SocialMediaItemProps {
  icon: AssetType
  platformName: string
  url: string
}

export interface SocialMediaRowProps {
  align: HeadingJustifyType
  socialMediaRowHeadline?: string
  socialMediaRow: Array<SocialMediaItemProps>
}

export interface FooterProps {
  id?: string
  componentName?: string | 'Footer'
  generalInformation: {
    title: string
    addressLine1: string
    addressLine2: string
    phoneNumber: string
  }
  legalText: string
  logoPrimary: {
    url: string
  }
  logoSecondary: {
    url: string
  }
  navMenu1: NavigationMenuItemProps[]
  navMenu2: NavigationMenuItemProps[]
  navMenu3: NavigationMenuItemProps[]
  navMenuLegal: NavigationMenuItemProps[]
  socialMediaRow: Array<SocialMediaItemProps>
  socialMediaRowHeadline: string
  imageLinkGroup?: ImageLinkGroupProps
}

export interface LeaderboardProps {
  id?: string
  educationLevel: number
  artWorkTitle: string
  schoolName: string
  firstName: string
  lastName: string
  posterImage: string
  voteCount: number
  _id: string
}

export interface GalleryPlaylistItem {
  id: string
  url: string
  videoTitle: string
  videoDescription?: string
  posterImage?: {
    url: string
    fileName: string
  }
  externalUrl?: string | null
  uploadDate?: string
  thumbnailUrl?: string
}

export interface GalleryProps {
  id?: string
  componentName?: string | 'Gallery'
  galleryPlaylist?: Array<GalleryPlaylistItem>
  tabLabels?: Array<string>
  resultsHeading: string
  activeTab?: string
  filterHeading: string
  filterValue: string
  sortByHeading: string
  sortByOptions: string[]
  isLeaderBoard: boolean
  heroTitle: string
  heroDescription: RichTextType
  heroBgImage?: AssetType
  votingModalReminder?: string
  votingModalDisclaimer?: string
  votingDeadline?: string
  groupVotingButton: IGroupVotingButton
  isWinnersView?: boolean
}

export interface CohortVotingButtonGroupProps {
  id?: string
  title: string
  votingButtons: IVotingButton[]
}

export interface IVideo {
  url: string
  posterImage: string
  title: string
  uploadDate: string
}

export interface IconButtonsProps {
  id: string
  enable: boolean
  icon: AssetType
  cta: string
  url: string
  openLinkInNewTab: boolean
}

export interface IconButtonsCardProps {
  id?: string
  componentName?: string | 'IconButtonsCard'
  enable: boolean
  eyebrow?: string
  headline?: string
  backgroundImage: AssetType
  backgroundVideo?: {
    url: string
  }
  iconButtons: Array<IconButtonsProps>
}

export type ModalViewType = ModalViewEventAnnouncementProps | ModalViewFormSuccessProps | ModalViewMediaProps

export interface ModalViewEventAnnouncementProps {
  componentName: 'ModalViewEventAnnouncement'
  id?: string
  eyebrowTitle?: string
  eyebrowBackgroundColor?: EyebrowBackgroundColorType | undefined
  headline: string
  description?: RichTextType
  disclaimer?: RichTextType
  buttonGroup: ButtonGroupProps
}

export interface ModalViewFormSuccessProps {
  componentName?: 'ModalViewFormSuccess'
  id?: string
  handleModalClose?: () => void
  ctas: ButtonProps[]
  description: RichTextType
  iconName: string
  title: string
  isBelowSm?: boolean | null
  isErrorView?: boolean
}

export interface ModalViewMediaProps {
  componentName: 'ModalViewMedia'
  id?: string
  mediaAsset?: AssetType
  mediaVideo?: VideoProps
  onMediaLoad?: () => void
}

export interface ModalProps {
  componentName?: string | 'Modal'
  id?: string
  backgroundColor?: 'black' | 'gray-800' | 'gray-900'
  backgroundImageXl?: AssetType | null
  backgroundImageSm?: AssetType | null
  view?: ModalViewType | undefined
  children?: React.ReactNode
  closeOnOutsideClick?: boolean
  handleModalClose?: () => void
  hasBorder?: boolean
  isOpen?: boolean
  overlayTheme?: 'dark' | 'blur'
  width: 'sm' | 'md' | 'md-legacy' | 'content-aware'
}

export interface DynamicComponentProps {
  block: ComponentItem
  isChildOfModal?: boolean
}

export interface EyebrowProps {
  id?: string
  backgroundColor?: EyebrowBackgroundColorType
  componentName?: string | 'Eyebrow'
  // Transformed into `color` within `enhanceComponentBlock`
  eyebrowColor?: EyebrowColorType
  // Transformed into `size` within `enhanceComponentBlock`
  eyebrowSize?: EyebrowSizeType
  isBold?: boolean
  isCornerTag?: boolean
  size?: EyebrowSizeType
  color?: EyebrowColorType
  justify?: string | 'center' | 'left'
  noMarginBottom?: boolean
  noMarginTop?: boolean
  title: string | JSX.Element | JSX.Element[]
}

export interface LostandFoundProps {
  id?: string
  componentName: string | 'LostandFound'
  description: RichTextType
  headline: string
  lostandfoundData: string | null
}

export interface ResponsiveMultimedia {
  backgroundVideoXl?: AssetVideoType
  backgroundVideoSm?: AssetVideoType
  posterImageXl?: AssetType
  posterImageSm?: AssetType
  videoLoop?: boolean
  vimeoIdXl?: string
  vimeoIdSm?: string
  allowPause?: boolean
  allowVolume?: boolean
}
export interface ResponsiveImage {
  imageMd?: AssetType
  imageSm?: AssetType
  imageXl: AssetType
}

export type GradientType = 'radial_gradient' | 'linear_gradient' | 'frosted_glass' | 'transparent'
export type CardBackgroundColorType =
  | 'tealDark'
  | 'pinkLight'
  | 'greenLight'
  | 'dark'
  | 'gold'
  | 'purple'
  | 'transparent'
export type GradientStartPositionType = 'left' | 'right'

export type EyebrowColorType = 'accent' | 'dark' | 'gray' | 'light' | 'lightest' | 'primary'
export type EyebrowBackgroundColorType = 'accent' | 'transparent' | 'orange' | 'primary' | 'purple' | 'secondary'
export type EyebrowSizeType = 'sm' | 'md' | 'lg' | 'xl'

export type HeadlineType = Pick<HeadingProps, 'levelDisplay' | 'level' | 'justify' | 'title'>

export type CardType = {
  id?: string
  backgroundColor?: CardBackgroundColorType
  backgroundGradient?: GradientType
  buttonGroup?: ButtonGroupProps
  centerCopyOnMobile?: boolean
  ctas?: ButtonProps[] // DynamicComponent transforms ctas into ButtonGroup
  description?: RichTextType
  eyebrow?: EyebrowProps
  hasDescriptionListCheckMark?: boolean
  hasLargeFontSizes?: boolean
  hasOverrideStyles?: boolean
  hasVideo?: boolean
  headline?: string
  image?: AssetType
  mediaPosition?: 'left' | 'right'
  overrideBackgroundColor?: CardBackgroundColorType
  overrideSubheadlineColor?: EyebrowColorType
  readMore?: RichTextType
  stackMediaAndCopy?: boolean
  subheadline?: string
  subheadlineColor?: EyebrowColorType
  video?: AssetVideoType
  dropdownButtonProps?: DropDownButtonProps
  overrideContainerClass?: string
  overrideBackgroundStyle?: React.CSSProperties
  isTestimonial?: boolean
  testimonial?: {
    hasQuoteSymbol?: boolean
    someQuoteSymbol?: boolean
    attributionImage?: AssetType
    attributionTitle?: string
    attributionDescription?: string
    companyLogo?: AssetType
  }
}

export type FoodType = {
  id?: string
  componentName?: string
  description?: RichTextType
  image?: AssetType
  headline?: string
  restaurants: RestaurantType[]
}

export type CardGroupType = {
  componentName?: 'CardGroup'
  id?: string
  backgroundGradient?: GradientType
  backgroundColor?: CardBackgroundColorType
  cards?: CardType[]
  cardsHaveLargeFontSizes?: boolean
  centerCopyOnMobile?: boolean
  headline?: HeadlineType
  isCarousel?: boolean
  isCarouselMobileOnly?: boolean
  stackMediaAndCopy?: boolean
  hasDescriptionListCheckMark?: boolean
  subheadlineColor?: EyebrowColorType
  slidesToShow?: number | null
  hasFilters?: boolean
  dataOptions?: FoodType[]
  enableModelSelection?: boolean
}

export type CardOverlayProps = {
  className?: string
  backgroundImage?: AssetType
  backgroundVideo?: VideoProps
  contentClassname?: string
  ctas?: Array<ButtonProps>
  description?: RichTextType
  eyebrow?: string
  hasVideo?: boolean
  headline?: HeadlineType
  headlineLogo?: AssetType
  id: string
  subheadline?: string
  subheadlineColor?: EyebrowColorType
  url?: string
  onCardClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  isCarouselSlide?: boolean
  hideContent?: boolean
  showModalOnClick?: boolean
}

export type CardOverlayGroupType = {
  subheadlineColor?: EyebrowColorType
  makeFirstCardTaller?: boolean
  cardsOverlay: CardOverlayProps[]
  hasFullWidth?: boolean
  removePadding?: boolean
}

export type AlignmentType = 'left' | 'center' | 'right'

export interface ImageLinkProps {
  id?: string
  imageXl?: AssetType
  isTargetBlank?: boolean
  title: string
  url?: string
  alignment?: AlignmentType
  linkLocation?: string
}

export interface ImageLinkGroupProps {
  images: ImageLinkProps[]
  alignment?: AlignmentType
  caption: RichTextType
  headline?: string
  displayImagesInline?: boolean
  isChildOfFooter?: boolean
  linkLocation?: string | null
}

export type IconNameType =
  | 'celebration'
  | 'grid'
  | 'mail'
  | 'work'
  | 'calendar'
  | 'chevron_right'
  | 'chevron_left'
  | 'chevron_down'
  | 'map'

export type IconNavType = {
  title: string
  iconName: IconNameType
  isActive?: boolean
  isDisabled?: boolean
  url?: string
  onClick?: () => void
}

export interface IconNavigationGroupProps {
  icons: IconNavType[]
  alignment?: AlignmentType
  removePadding?: boolean
  isLoading: boolean
  parentLinkModule?: string
  theme?: {
    activeBackgroundColor?: 'lightest'
    hoverBackgroundColor?: 'primary-dark' | 'lightest'
  }
}

export type TaxonomyNodeType = {
  path?: { value: string }[]
  value: string
}

export interface DropDownOption {
  label?: string
  value?: string
}
export interface MenuDropDownOption extends DropDownOption {
  mediaAsset?: AssetType
  restaurantId?: string
  artistId?: string
}

export type DropDownType = 'foodRestriction' | 'restaurantLocation' | string

export interface BaseDropDownData {
  id?: string
  dropDownOption: DropDownType
  foodRestrictions?: TaxonomyNodeType[]
  restaurantLocations?: TaxonomyNodeType[]
}

export interface DropDownProps {
  id?: string
  dropDownOption?: DropDownType
  options?: DropDownOption[]
  placeholder?: string
  onDropDownChange?: (value: string, type: string) => void
  theme: 'light' | 'muted'
  isLoading?: boolean
  showPlaceholderInMenu?: boolean
  initialSelectedValue?: string
  highlightSelectedValue?: boolean
}

export interface NormalizedDropDown {
  id?: string
  dropDownOption?: DropDownType
  options?: DropDownOption[]
}

export interface DropDownGroupProps {
  dropDowns?: NormalizedDropDown[]
  onDropDownChange?: (value: string, type: string) => void
  theme?: 'light' | 'muted'
  isLoading?: boolean
  showPlaceholderInMenu?: boolean
}

export type MenuType = {
  id: string
  image: AssetType
  artist?: {
    id?: string
    artistName?: string
  }
}
export type RestaurantType = {
  id?: string
  componentName?: string
  description?: RichTextType
  foodRestriction?: TaxonomyNodeType[]
  image?: AssetType
  headline?: string
  restaurantLocation?: TaxonomyNodeType[]
  subheadline?: string
  menus?: MenuType[]
  mapLocation?: AssetType
  eyebrow?: EyebrowProps
}
export interface CardGroupWithDropDownFilterProps {
  headline?: HeadlineType
  dropDownGroup?: {
    dropDowns?: BaseDropDownData[]
  }
  dataOptions?: RestaurantType[]
  cardGroup?: CardGroupType
}

export interface DropDownButtonProps {
  buttonProps?: ButtonProps
  dropDownProps?: DropDownProps
}

// @TODO: continue to build upon this type
export type ComponentItem =
  | ArtistIntroCardType
  | ButtonProps
  | CalendarSingleArtistProps
  | CardGroupType
  | CarouselSlideProps
  | ContentModuleProps
  | EyebrowProps
  | FooterProps
  | FormProps
  | GalleryProps
  | HeroGeneralInfoProps
  | LostandFoundProps
  | HeadingProps
  | IconButtonsCardProps
  | ImageProps
  | ModalProps
  | ModalViewEventAnnouncementProps
  | ModalViewFormSuccessProps
  | NavigationHeaderProps
  | ParagraphProps
